import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FoodAllowance.css";
import logo from "../../assets/WHL-LOGO.png";
import axiosInstance from "../../services/auth";
import { toast, ToastContainer } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useCombinedContext } from "../../context/CombinedContext";

const FoodPaymentReport = ({
  formatPrice,
  foodallowance,
  onClose,
  companyId,
  onUpdate,
}) => {
  const { selectedCompany } = useCombinedContext();
  const company = selectedCompany;

  const totalRepayment = foodallowance.paidDetails
    ? foodallowance.paidDetails.reduce((acc, cur) => acc + (cur.amount || 0), 0)
    : 0;
  const balanceRepayment = foodallowance.foodTotal - totalRepayment;

  const [selectedFoodDetail, setSelectedFoodDetail] = useState(null);
  const [localFoodAllowance, setLocalFoodAllowance] = useState(foodallowance);

  const [sortOrder, setSortOrder] = useState("asc");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState("payment");

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  const handleEditClick = (foodDetail) => {
    setSelectedFoodDetail(foodDetail);
    setEditData({
      month: foodDetail.month,
      year: foodDetail.year,
      date: foodDetail.date?.substring(0, 10),
      amount: foodDetail.amount,
    });
    setIsEditModalOpen(true);
  };

  const totalPaidAmount = useMemo(() => {
    return localFoodAllowance.paidDetails.reduce(
      (acc, curr) => acc + (Number(curr.amount) || 0),
      0,
    );
  }, [localFoodAllowance.paidDetails]);

  const balanceAmount = useMemo(() => {
    return (localFoodAllowance.foodTotal || 0) - totalPaidAmount;
  }, [localFoodAllowance.foodTotal, totalPaidAmount]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(
        `/foodallowance/${foodallowance._id}/${deleteTarget}`,
      );
      toast.success("Food detail deleted successfully");
      setIsDeleteModalOpen(false);

      // ✅ Update local state after delete
      setLocalFoodAllowance((prev) => {
        const updatedPaidDetails = prev.paidDetails.filter(
          (item) => item._id !== deleteTarget,
        );
        return {
          ...prev,
          paidDetails: updatedPaidDetails,
        };
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to delete food detail");
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const sortedPaymentDetails = localFoodAllowance.paidDetails
    ? [...localFoodAllowance.paidDetails].sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return sortOrder === "asc"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      })
    : [];

  // const handleEditSubmit = async (e) => {
  //   e.preventDefault();
  //   try {
  //     const res = await axiosInstance.patch(
  //       `/foodallowance/payment/${foodallowance._id}/${selectedFoodDetail._id}`,
  //       editData
  //     );
  //     toast.success("Payment updated successfully");
  //     setIsEditModalOpen(false);
  //     if (onUpdate) onUpdate();
  //   } catch (err) {
  //     toast.error("Update failed");
  //   }
  // };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axiosInstance.patch(
        `/foodallowance/payment/${foodallowance._id}/${selectedFoodDetail._id}`,
        editData,
      );
      toast.success("Payment updated successfully");
      setIsEditModalOpen(false);

      // Update payment details in local state
      setLocalFoodAllowance((prev) => {
        const updatedPaidDetails = prev.paidDetails.map((item) =>
          item._id === selectedFoodDetail._id ? { ...item, ...editData } : item,
        );

        const updatedMonthlyBalances = prev.monthlyBalances.map((item) => {
          if (item._id === selectedFoodDetail.monthlyBalanceId) {
            const updatedPaidTotal =
              (item.paidTotal || 0) -
              (selectedFoodDetail.amount || 0) +
              Number(editData.amount || 0);
            return {
              ...item,
              paidTotal: updatedPaidTotal,
              balance:
                (item.foodTotal || 0) +
                (item.allowanceTotal || 0) -
                updatedPaidTotal,
            };
          }
          return item;
        });

        return {
          ...prev,
          paidDetails: updatedPaidDetails,
          monthlyBalances: updatedMonthlyBalances,
        };
      });
    } catch (err) {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    setLocalFoodAllowance(foodallowance);
  }, [foodallowance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        onClick={handleOutsideClick}
        style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content foodallowance_model">
            <div className="modal-header text-white text-center vesel_form">
              <h5 className="modal-title">Food Payment Report</h5>
              <button
                type="button"
                className="btn-close text-white"
                onClick={onClose}
              ></button>
            </div>
            <div ref={printRef} className="modal-body modalWhite">
              <div className="food_head" style={{ marginBottom: "25px" }}>
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={logo}
                    alt="logo"
                    style={{
                      width: "125px",
                      height: "auto",
                      paddingRight: "20px",
                    }}
                  />
                </div>
                <div style={{ textAlign: "left", flex: "1", padding: "0 5px" }}>
                  <h2
                    className="company_font"
                    style={{ margin: 0, textDecoration: "underline" }}
                  >
                    {company.company_name}
                  </h2>
                  <p style={{ margin: 0, fontSize: "12px" }}>
                    {`Co Reg No: ${company.co_regno} GST No: ${company.gst_no}`}
                    <br />
                    {company.address}
                    <br />
                    {`Tel: ${company.telephone} Fax: ${company.fax}`}
                    <br />
                    {company.email}
                  </p>
                </div>
              </div>
              <hr />
              <div>
                <p className="mt-3">
                  <strong>Employee Name:</strong> {foodallowance.employee?.name}
                </p>
                <p>
                  <strong>Employee ID No:</strong>{" "}
                  {foodallowance.employee?.employeeNo}
                </p>
                <ul className="nav nav-tabs mb-2 mt-4">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "payment" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("payment")}
                    >
                      Payment
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "monthlybalance" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("monthlybalance")}
                    >
                      Monthly Report
                    </button>
                  </li>
                </ul>
                {activeTab === "payment" && (
                  <>
                    <div className="d-flex align-items-center justify-content-end mb-3">
                      <button
                        className=" button-2 for_print"
                        onClick={toggleSortOrder}
                      >
                        Sort by Date:{" "}
                        {sortOrder === "asc" ? "Ascending" : "Descending"}
                      </button>
                    </div>
                    {sortedPaymentDetails.length > 0 ? (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Month</th>
                            <th>Year</th>
                            <th>Amount</th>
                            <th className="for_print">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPaymentDetails.map((detail) => (
                            <tr key={detail._id}>
                              <td>
                                {detail.date
                                  ? new Date(detail.date).toLocaleDateString()
                                  : "N/A"}
                              </td>
                              <td>{detail.month || "N/A"}</td>
                              <td>{detail.year || "N/A"}</td>
                              <td>
                                <div className="d-flex justify-content-between">
                                  <span>$</span>
                                  <span>
                                    {formatPrice(detail.amount) || "00"}
                                  </span>
                                </div>
                              </td>
                              <td className="for_print">
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => handleEditClick(detail)}
                                  >
                                    <i className="bi bi-pencil-square"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => {
                                      setDeleteTarget(detail._id);
                                      setIsDeleteModalOpen(true);
                                    }}
                                  >
                                    <i className="bi bi-trash3"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>No food Payment available.</p>
                    )}
                  </>
                )}
                {activeTab === "monthlybalance" && (
                  <>
                    {foodallowance.monthlyBalances &&
                    foodallowance.monthlyBalances.length > 0 ? (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th>Food Total</th>
                            <th>Allowance Total</th>
                            <th>Paid Total</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {foodallowance.monthlyBalances.map((detail) => (
                            <tr key={detail._id}>
                              <td>
                                {(() => {
                                  // detail.month is "2025-03"
                                  const [year, mon] = detail.month.split("-");
                                  // Create a Date so we can derive the localized month name:
                                  const monthName = new Date(
                                    +year,
                                    +mon - 1,
                                  ).toLocaleString("default", {
                                    month: "long",
                                  });
                                  return `${monthName} ${year}`;
                                })()}
                              </td>

                              <td>
                                <div className="d-flex ">
                                  <span>$</span>
                                  <span>
                                    {formatPrice(detail.foodTotal) || "00"}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex ">
                                  <span>$</span>
                                  <span>
                                    {formatPrice(detail.allowanceTotal) || "00"}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex ">
                                  <span>$</span>
                                  <span>
                                    {formatPrice(detail.paidTotal) || "00"}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex ">
                                  <span>$</span>
                                  <span>
                                    {formatPrice(detail.balance) || "00"}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>Data not available.</p>
                    )}
                  </>
                )}

                <p>
                  <strong>Total Allowance:</strong> ${" "}
                  {formatPrice(foodallowance.allowanceTotal)}
                </p>
                <p>
                  <strong>Total Amount:</strong> ${" "}
                  {formatPrice(foodallowance.foodTotal)}
                </p>
                <p>
                  <strong>Total Paid Amount:</strong> ${" "}
                  {totalPaidAmount.toFixed(2)}
                </p>
                <p>
                  <strong>Balance Amount:</strong> $ {balanceAmount.toFixed(2)}
                </p>

                {/* <div className="mt-3">
  <p><strong>Total Allowance: $ </strong>{localFoodAllowance.allowanceAmount?.toFixed(2) || "0.00"}</p>
  <p><strong>Total Amount: $</strong> {localFoodAllowance.totalAmount?.toFixed(2) || "0.00"}</p>
  <p><strong>Total Paid Amount: $</strong>{totalPaidAmount.toFixed(2)}</p>
  <p><strong>Balance Amount: $</strong> {balanceAmount.toFixed(2)}</p>
</div> */}
              </div>
            </div>
            <div className="text-center">
              <button className="button-2 mb-5" onClick={handlePrint}>
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target.classList.contains("modal"))
              setIsDeleteModalOpen(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  className="btn-close"
                  onClick={() => setIsDeleteModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this food detail?
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target.classList.contains("modal")) setIsEditModalOpen(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleEditSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Edit Payment</h5>
                  <button
                    className="btn-close"
                    onClick={() => setIsEditModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={editData.date}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Month</label>
                    <input
                      type="text"
                      className="form-control"
                      name="month"
                      value={editData.month}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Year</label>
                    <input
                      type="text"
                      className="form-control"
                      name="year"
                      value={editData.year}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={editData.amount}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsEditModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default FoodPaymentReport;
